import React, { memo, useCallback, useMemo } from 'react';
import { useTracker } from '@dpdgroupuk/react-event-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { Divider, Button } from '@mui/material';
import { makeStyles } from '@mui/styles';

import palette from '../components/styles/palette';
import FilterPanel from '../components/FilterPanel';
import { FilterTypes as RouteFilterTypes } from '../models/route';
import { FilterTypes as StopFilterTypes } from '../models/stop';
import routeFilters from '../enums/routeFilters';
import stopFilters from '../enums/stopFilters';
import useLocationQueryParams from '../hooks/useLocationQueryParams';
import { FILTERS, STATUS_FILTER } from '../constants/analytics';
import {
  ALL,
  STOP_FILTER,
  ROUTE_FILTER,
  SELECT_STOPS,
} from '../constants/strings';
import {
  onChangeRouteFilter,
  onChangeStopFilter,
  selectFilteredStops,
} from '../store/actions/ui/filterPanel';
import {
  getSelectedRouteFilters,
  getSelectedStopFilters,
} from '../store/selectors/ui/filterPanel';
import { View } from '../components/Base';
import { setStopMarkerPopup } from '../store/reducers/map';

const useStyles = makeStyles(({ typography }) => ({
  container: {
    paddingTop: typography.pxToRem(18),
    textAlign: 'center',
  },
  divider: {
    height: '0.063rem',
    background: palette.typography.white.ghost,
  },
  selectButton: {
    borderRadius: 4,
    border: `1px solid ${palette.typography.white.solid}`,
    padding: `${typography.pxToRem(5)} ${typography.pxToRem(10)}`,
    textTransform: 'capitalize',
    height: typography.pxToRem(30),
    fontSize: typography.pxToRem(14),
    boxSizing: 'border-box',
    boxShadow: '0px 3px 5px 1px rgba(0, 0, 0, 0.2)',
    marginTop: typography.pxToRem(18),
    color: palette.typography.white.solid,
    width: '70%',
  },
}));

const FilterPanelContainer = ({ activeRoute }) => {
  const styles = useStyles();
  const query = useLocationQueryParams();
  const dispatch = useDispatch();
  const selectedFilters = useSelector(
    activeRoute ? getSelectedStopFilters : getSelectedRouteFilters
  );
  const filterConfig = useMemo(
    () => ({
      header: activeRoute ? STOP_FILTER : ROUTE_FILTER,
      filterTypes: activeRoute ? StopFilterTypes : RouteFilterTypes,
      filters: activeRoute ? stopFilters : routeFilters,
      onChange: activeRoute ? onChangeStopFilter : onChangeRouteFilter,
    }),
    [activeRoute]
  );
  const tracker = useTracker();

  const handleClickFilterPanelButton = useCallback(
    (isOpen) => {
      tracker.logEvent(
        FILTERS[isOpen ? 'SELECT_HIDE_FILTERS' : 'SELECT_SHOW_FILTERS']
      );
      dispatch(setStopMarkerPopup(null));
    },
    [tracker, dispatch]
  );

  const handleOnChangeFilter = useCallback(
    (field, label, values) => {
      const isAll = label === ALL;

      STATUS_FILTER[field] &&
        tracker.logEvent({
          actionId: STATUS_FILTER[field][(isAll && 'all') || 'single'],
          value: label,
        });
      dispatch(filterConfig.onChange(field, label, values));
    },
    [filterConfig, dispatch, tracker]
  );

  const handleOnLoad = useCallback(() => {
    tracker.logEvent(FILTERS.LOAD);
  }, [tracker]);

  const onSelectStops = useCallback(
    () => dispatch(selectFilteredStops()),
    [dispatch]
  );

  return (
    <FilterPanel
      filterConfig={filterConfig}
      hide={query.detailVisible}
      onChange={handleOnChangeFilter}
      onClickFilterPanelButton={handleClickFilterPanelButton}
      onLoad={handleOnLoad}
      selectedFilters={selectedFilters}
      activeRoute={activeRoute}
    >
      {!!activeRoute && (
        <View className={styles.container}>
          <Divider className={styles.divider} />
          <Button className={styles.selectButton} onClick={onSelectStops}>
            {SELECT_STOPS}
          </Button>
        </View>
      )}
    </FilterPanel>
  );
};

export default memo(FilterPanelContainer);
